/**
 * hr moduli uchun umumiy turlar/konstantalar (API + veb + mobil).
 * Faqat hr moduli egasi tahrirlaydi.
 *
 * Xodim holati (`employee`) va davomat holati (`attendance`) — umumiy `statuses.ts` da
 * (StatusPill shu guruhlardan foydalanadi). Bu yerda — HR ga xos qolgan ro'yxatlar.
 */
import type { L10n } from '../i18n';
import type { Tone } from '../statuses';

/** Ko'p tilli nom + rang ohangi (UI badge uchun). */
export interface HrOption extends L10n {
  tone: Tone;
}

type OptionMap<K extends string> = Record<K, HrOption>;
const opts = <K extends string>(m: OptionMap<K>) => m;

/** Ro'yxatni select/tab variantlariga aylantirish. */
export function optionList<K extends string>(m: OptionMap<K>): { value: K; label: string; tone: Tone }[] {
  return (Object.keys(m) as K[]).map((value) => ({ value, label: m[value].uz, tone: m[value].tone }));
}

// ------------------------------------------------------------------ Xodim kartochkasi
export const EMPLOYMENT_TYPES = opts({
  full_time: { uz: 'To‘liq stavka', ru: 'Полная занятость', en: 'Full time', zh: '全职', tone: 'success' },
  part_time: { uz: 'Yarim stavka', ru: 'Частичная занятость', en: 'Part time', zh: '兼职', tone: 'info' },
  contract: { uz: 'Shartnoma (GPX)', ru: 'Договор ГПХ', en: 'Contractor', zh: '劳务合同', tone: 'violet' },
  daily: { uz: 'Kunbay', ru: 'Подённо', en: 'Day rate', zh: '日结', tone: 'warning' },
});
export type EmploymentType = keyof typeof EMPLOYMENT_TYPES;
export const EMPLOYMENT_TYPE_KEYS = Object.keys(EMPLOYMENT_TYPES) as EmploymentType[];

export const GENDERS = opts({
  male: { uz: 'Erkak', ru: 'Мужской', en: 'Male', zh: '男', tone: 'neutral' },
  female: { uz: 'Ayol', ru: 'Женский', en: 'Female', zh: '女', tone: 'neutral' },
});
export type Gender = keyof typeof GENDERS;
export const GENDER_KEYS = Object.keys(GENDERS) as Gender[];

/** Xodim holatlari (`statuses.ts` dagi EMPLOYEE_STATUSES bilan bir xil kalitlar). */
export const EMPLOYEE_STATUS_KEYS = ['active', 'probation', 'on_leave', 'dismissed'] as const;
export type EmployeeStatusKey = (typeof EMPLOYEE_STATUS_KEYS)[number];

export const DOCUMENT_KINDS = opts({
  passport: { uz: 'Pasport / ID karta', ru: 'Паспорт / ID-карта', en: 'Passport / ID card', zh: '护照 / 身份证', tone: 'info' },
  contract: { uz: 'Mehnat shartnomasi', ru: 'Трудовой договор', en: 'Employment contract', zh: '劳动合同', tone: 'accent' },
  diploma: { uz: 'Diplom', ru: 'Диплом', en: 'Diploma', zh: '学历证书', tone: 'violet' },
  medical: { uz: 'Tibbiy ma’lumotnoma', ru: 'Медицинская справка', en: 'Medical certificate', zh: '健康证明', tone: 'success' },
  other: { uz: 'Boshqa', ru: 'Другое', en: 'Other', zh: '其他', tone: 'neutral' },
});
export type DocumentKind = keyof typeof DOCUMENT_KINDS;
export const DOCUMENT_KIND_KEYS = Object.keys(DOCUMENT_KINDS) as DocumentKind[];

export const SALARY_KINDS = opts({
  monthly: { uz: 'Oylik (oklad)', ru: 'Оклад (месяц)', en: 'Monthly salary', zh: '月薪', tone: 'accent' },
  daily: { uz: 'Kunlik stavka', ru: 'Дневная ставка', en: 'Daily rate', zh: '日薪', tone: 'info' },
  hourly: { uz: 'Soatbay stavka', ru: 'Почасовая ставка', en: 'Hourly rate', zh: '时薪', tone: 'violet' },
});
export type SalaryKind = keyof typeof SALARY_KINDS;
export const SALARY_KIND_KEYS = Object.keys(SALARY_KINDS) as SalaryKind[];

// ------------------------------------------------------------------ Xodim faolligi («Men» kalendari)
/**
 * FAOLLIK METRIKASI — W ilovasidagi «Men» bo'limining kalendar-issiqlik xaritasi
 * (`GET /hr/me/activity`). Bitta ekran har xil hodimga ishlaydi: server hodimning
 * RUXSATI bo'yicha metrikani o'zi tanlaydi, ilova faqat sonlarni chizadi.
 *
 * Tartib (birinchi mos kelgani): `delivery.courier` → delivered · `wms.assemble` → assembled ·
 * `wms.pick` → picked · aks holda → attendance.
 */
export const ACTIVITY_METRICS = opts({
  delivered: { uz: 'Yetkazilgan buyurtmalar', ru: 'Доставленные заказы', en: 'Orders delivered', zh: '已配送订单', tone: 'success' },
  assembled: { uz: 'Yig‘ilgan buyurtmalar', ru: 'Собранные заказы', en: 'Orders assembled', zh: '已组装订单', tone: 'accent' },
  picked: { uz: 'Yig‘ilgan tovar satrlari', ru: 'Собранные строки товаров', en: 'Items picked', zh: '已拣货行', tone: 'info' },
  attendance: { uz: 'Ishlagan kunlar', ru: 'Отработанные дни', en: 'Days worked', zh: '出勤天数', tone: 'violet' },
});
export type ActivityMetric = keyof typeof ACTIVITY_METRICS;
export const ACTIVITY_METRIC_KEYS = Object.keys(ACTIVITY_METRICS) as ActivityMetric[];
/** Kalendar bir so'rovda ko'rsata oladigan eng ko'p oy soni. */
export const ACTIVITY_MAX_MONTHS = 12;

// ------------------------------------------------------------------ Ta'til
export const LEAVE_KINDS = opts({
  vacation: { uz: 'Mehnat ta’tili', ru: 'Ежегодный отпуск', en: 'Annual leave', zh: '年假', tone: 'info' },
  unpaid: { uz: 'Haqsiz ta’til', ru: 'Отпуск без содержания', en: 'Unpaid leave', zh: '无薪假', tone: 'neutral' },
  sick: { uz: 'Kasallik varaqasi', ru: 'Больничный', en: 'Sick leave', zh: '病假', tone: 'warning' },
});
export type LeaveKind = keyof typeof LEAVE_KINDS;
export const LEAVE_KIND_KEYS = Object.keys(LEAVE_KINDS) as LeaveKind[];

export const LEAVE_STATUSES = opts({
  planned: { uz: 'Rejada', ru: 'Запланирован', en: 'Planned', zh: '已计划', tone: 'info' },
  approved: { uz: 'Tasdiqlangan', ru: 'Утверждён', en: 'Approved', zh: '已批准', tone: 'success' },
  rejected: { uz: 'Rad etilgan', ru: 'Отклонён', en: 'Rejected', zh: '已驳回', tone: 'danger' },
});
export type LeaveStatus = keyof typeof LEAVE_STATUSES;
export const LEAVE_STATUS_KEYS = Object.keys(LEAVE_STATUSES) as LeaveStatus[];
/** Davomat rejasiga ta'sir qiladigan ta'til holatlari (rad etilgani hisobga olinmaydi). */
export const LEAVE_EFFECTIVE_STATUSES: LeaveStatus[] = ['planned', 'approved'];

// ------------------------------------------------------------------ Jadval
export const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;
export const WEEKDAY_NAMES: Record<number, string> = {
  1: 'Dushanba',
  2: 'Seshanba',
  3: 'Chorshanba',
  4: 'Payshanba',
  5: 'Juma',
  6: 'Shanba',
  7: 'Yakshanba',
};
export const WEEKDAY_SHORT: Record<number, string> = {
  1: 'Du',
  2: 'Se',
  3: 'Cho',
  4: 'Pa',
  5: 'Ju',
  6: 'Sha',
  7: 'Ya',
};

/** Kun rejasi holati. */
export const PLAN_STATES = opts({
  planned: { uz: 'Ish kuni', ru: 'Рабочий день', en: 'Working day', zh: '工作日', tone: 'accent' },
  rest: { uz: 'Dam kuni', ru: 'Выходной', en: 'Day off', zh: '休息日', tone: 'neutral' },
  unset: { uz: 'Jadval yo‘q', ru: 'Без графика', en: 'No schedule', zh: '无排班', tone: 'neutral' },
});
export type PlanState = keyof typeof PLAN_STATES;

/** Rejaning manbai (ustunlik tartibida: kun rejasi > ta'til > bayram > haftalik jadval). */
export const PLAN_SOURCES = opts({
  day: { uz: 'Kun rejasi', ru: 'План дня', en: 'Day plan', zh: '当日计划', tone: 'violet' },
  leave: { uz: 'Ta’til', ru: 'Отпуск', en: 'Leave', zh: '休假', tone: 'info' },
  holiday: { uz: 'Bayram', ru: 'Праздник', en: 'Public holiday', zh: '节假日', tone: 'violet' },
  weekly: { uz: 'Haftalik jadval', ru: 'Недельный график', en: 'Weekly schedule', zh: '周排班', tone: 'neutral' },
});
export type PlanSource = keyof typeof PLAN_SOURCES;

// ------------------------------------------------------------------ Qurilmalar va hodisalar
export const TRUST_TIME_MODES = opts({
  inherit: { uz: 'Umumiy sozlama bo‘yicha', ru: 'По общей настройке', en: 'Per global setting', zh: '按全局设置', tone: 'neutral' },
  yes: { uz: 'Qurilma soatiga ishonish', ru: 'Доверять часам устройства', en: 'Trust the device clock', zh: '以设备时间为准', tone: 'warning' },
  no: { uz: 'Server vaqti', ru: 'Время сервера', en: 'Server time', zh: '服务器时间', tone: 'success' },
});
export type TrustTimeMode = keyof typeof TRUST_TIME_MODES;
export const TRUST_TIME_KEYS = Object.keys(TRUST_TIME_MODES) as TrustTimeMode[];

/** Saqlanadigan davomat belgisi (Hikvision `attendanceStatus` normallashtirilgani). */
export const ATTEND_STATUSES = opts({
  checkIn: { uz: 'Kirish', ru: 'Приход', en: 'Clock-in', zh: '上班打卡', tone: 'success' },
  checkOut: { uz: 'Chiqish', ru: 'Уход', en: 'Clock-out', zh: '下班打卡', tone: 'info' },
  breakOut: { uz: 'Tushlikka chiqish', ru: 'Уход на обед', en: 'Break start', zh: '午休开始', tone: 'warning' },
  breakIn: { uz: 'Tushlikdan qaytish', ru: 'Возврат с обеда', en: 'Break end', zh: '午休结束', tone: 'warning' },
  unknown: { uz: 'Belgisiz', ru: 'Без отметки', en: 'Unmarked', zh: '未标记', tone: 'neutral' },
});
export type AttendStatus = keyof typeof ATTEND_STATUSES;
export const ATTEND_STATUS_KEYS = Object.keys(ATTEND_STATUSES) as AttendStatus[];

export const EVENT_SOURCES = opts({
  device: { uz: 'FaceID qurilma', ru: 'FaceID терминал', en: 'Face ID device', zh: '人脸识别设备', tone: 'accent' },
  manual: { uz: 'Qo‘lda', ru: 'Вручную', en: 'Manual', zh: '手动', tone: 'warning' },
  app: { uz: 'W ilova', ru: 'Приложение W', en: 'W app', zh: 'W 应用', tone: 'info' },
});
export type EventSource = keyof typeof EVENT_SOURCES;

export const RAW_EVENT_STATUSES = opts({
  accepted: { uz: 'Qabul qilindi', ru: 'Принято', en: 'Accepted', zh: '已接收', tone: 'success' },
  duplicate: { uz: 'Takror', ru: 'Дубликат', en: 'Duplicate', zh: '重复', tone: 'neutral' },
  skipped: { uz: 'O‘tkazib yuborildi', ru: 'Пропущено', en: 'Skipped', zh: '已跳过', tone: 'warning' },
  error: { uz: 'Xato', ru: 'Ошибка', en: 'Error', zh: '错误', tone: 'danger' },
});
export type RawEventStatus = keyof typeof RAW_EVENT_STATUSES;

/** Xom paket o'tkazib yuborilishi sabablari (qurilma loglari). */
export const RAW_SKIP_REASONS: Record<string, string> = {
  heartbeat: 'Qurilma signali (davomat hodisasi emas)',
  not_access_event: 'Kirish nazorati hodisasi emas',
  no_employee: 'Xodim aniqlanmagan (notanish yuz yoki muvaffaqiyatsiz urinish)',
  denied_minor_type: 'Hodisa turi qora ro‘yxatda',
  not_allowed_minor_type: 'Hodisa turi ruxsat ro‘yxatida yo‘q',
  unknown_device: 'Qurilma ro‘yxatga olinmagan — filialga biriktiring',
  device_disabled: 'Qurilma o‘chirilgan',
  branch_inactive: 'Filial faolsizlantirilgan — o‘tishlar yozilmaydi',
  parse_error: 'Paket o‘qilmadi (JSON/XML buzuq)',
  read_error: 'So‘rovni o‘qib bo‘lmadi',
  bad_token: 'Noto‘g‘ri yoki yo‘q token',
  window: 'Takroriy o‘tish (qisqa vaqt oynasi ichida)',
  same_packet: 'Aynan shu paket avval qabul qilingan',
};

// ------------------------------------------------------------------ Belgilash xatolari
export const PUNCH_ANOMALY_KINDS: Record<string, { label: string; hint: string; scope: 'work' | 'break' }> = {
  out_first: {
    label: 'Kirish o‘rniga chiqish belgilandi',
    hint: 'Ishga kelganda «Check Out» bosilgan. To‘g‘risi — «Check In».',
    scope: 'work',
  },
  in_repeat: {
    label: 'Chiqish belgilanmay yana kirish',
    hint: 'Oldingi kirishdan keyin chiqish belgilanmagan. Ketishda «Check Out» bosilishi kerak.',
    scope: 'work',
  },
  out_repeat: {
    label: 'Chiqish ikki marta belgilandi',
    hint: 'Chiqish allaqachon belgilangan edi — yana «Check Out» bosilgan.',
    scope: 'work',
  },
  break_in_first: {
    label: 'Tushlik: chiqmay «qaytdim» belgilandi',
    hint: 'Tushlikka chiqishda «Break Out», qaytishda «Break In» bosiladi.',
    scope: 'break',
  },
  break_out_repeat: {
    label: 'Tushlik: qaytish belgilanmay yana chiqish',
    hint: 'Tushlikdan qaytishda «Break In» bosilishi kerak.',
    scope: 'break',
  },
};

export function anomalyLabel(kind: string): string {
  return PUNCH_ANOMALY_KINDS[kind]?.label ?? kind;
}

// ------------------------------------------------------------------ Ish haqi
export const PAYROLL_STATUSES = opts({
  draft: { uz: 'Qoralama', ru: 'Черновик', en: 'Draft', zh: '草稿', tone: 'neutral' },
  calculated: { uz: 'Hisoblandi', ru: 'Рассчитан', en: 'Calculated', zh: '已核算', tone: 'info' },
  approved: { uz: 'Tasdiqlandi', ru: 'Утверждён', en: 'Confirmed', zh: '已确认', tone: 'success' },
  paid: { uz: 'To‘landi', ru: 'Выплачен', en: 'Paid', zh: '已支付', tone: 'accent' },
});
export type PayrollStatus = keyof typeof PAYROLL_STATUSES;
/** Tasdiqlangan/to'langan davr qulflanadi (qayta hisoblash va tahrir yo'q). */
export const PAYROLL_LOCKED: PayrollStatus[] = ['approved', 'paid'];

export const ADJUSTMENT_KINDS = opts({
  bonus: { uz: 'Mukofot', ru: 'Премия', en: 'Bonus', zh: '奖金', tone: 'success' },
  fine: { uz: 'Jarima', ru: 'Штраф', en: 'Penalty', zh: '罚款', tone: 'danger' },
  advance: { uz: 'Avans', ru: 'Аванс', en: 'Advance', zh: '预支', tone: 'warning' },
});
export type AdjustmentKind = keyof typeof ADJUSTMENT_KINDS;
export const ADJUSTMENT_KIND_KEYS = Object.keys(ADJUSTMENT_KINDS) as AdjustmentKind[];

// ------------------------------------------------------------------ Sozlamalar
export interface HrSettings {
  /** Qurilma soatiga ishonish (qurilma `inherit` holatida). Sukut — server vaqti. */
  trustDeviceTime: boolean;
  /** Qurilma vaqti server vaqtidan shuncha daqiqadan ko'p farq qilsa — server vaqti olinadi. */
  maxDriftMin: number;
  /** Shu soniya ichidagi bir xil belgili takroriy skan — bitta o'tish. */
  dupWindowSec: number;
  /** Kechikish shu daqiqagacha hisoblanmaydi (undan oshsa — to'liq kechikish). */
  lateGraceMin: number;
  /** Kunlik davomat jamlanmasi yuboriladigan soat (Toshkent). */
  dailySummaryHour: number;
  /** Ish haqida kechikish uchun avtomatik jarima, so'm/daqiqa (0 — o'chiq). */
  lateFinePerMinute: number;
  /** Qurilmaning shu `minorEventType` kodlari tashlanadi. */
  denyMinorTypes: number[];
  /** Bo'sh bo'lmasa — faqat shu kodlar qabul qilinadi. */
  allowMinorTypes: number[];
}

export const HR_SETTINGS_DEFAULTS: HrSettings = {
  trustDeviceTime: false,
  maxDriftMin: 720,
  dupWindowSec: 15,
  lateGraceMin: 0,
  dailySummaryHour: 19,
  lateFinePerMinute: 0,
  denyMinorTypes: [],
  allowMinorTypes: [],
};

/** Qurilma tokeni almashtirilganda eski token shuncha daqiqa ishlaydi (sukut). */
export const DEVICE_TOKEN_GRACE_MIN = 60;
/** Xom paketlar shuncha kun saqlanadi. */
export const RAW_EVENTS_RETENTION_DAYS = 14;

// ------------------------------------------------------------------ Kirish huquqi
/**
 * HR menejer xodimga bera oladigan rollar (operatsion). Boshqaruv rollari
 * (admin, buxgalter, HR va h.k.) faqat `users.manage` egasi yoki super admin tomonidan beriladi.
 */
export const HR_ASSIGNABLE_ROLES = [
  'employee',
  'stocker',
  'packer',
  'courier',
  'operator',
  'cashier',
  'logist',
  'support_operator',
] as const;
